/*
 * seo/usePageMeta.ts — React binding over the head manager.
 *
 * Applies the per-route head tags in an effect and restores the previous state on
 * unmount / dependency change. Effects (not render) touch the DOM, so this is SSR
 * / test-render safe. The JSON-LD object is compared by its serialized form so a
 * fresh object literal on every render does not thrash the effect.
 */
import { useEffect } from 'react';
import { applyPageMeta } from './head-manager';
import type { PageMetaOptions } from './types';

export function usePageMeta(options: PageMetaOptions): void {
  const { title, description, canonicalPath, ogType, image, noindex, jsonLd } = options;
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : '';

  useEffect(() => {
    if (typeof document === 'undefined') return;
    return applyPageMeta(document, {
      title,
      description,
      canonicalPath,
      ogType,
      image,
      noindex,
      jsonLd,
    });
    // `jsonLdKey` stands in for `jsonLd` (stable across identical object literals).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonicalPath, ogType, image, noindex, jsonLdKey]);
}
